import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import DataTableExtensions from 'react-data-table-component-extensions';
import 'react-data-table-component-extensions/dist/index.css';
import { FaEdit, FaTrash, FaBan, FaCheck, FaPlus, FaTimes, FaKey, FaUserTimes, FaUndo } from 'react-icons/fa';
import api from './api';
import useAuth from '../hooks/useAuth';
import { PERMISSION_GROUPS, PERMISSION_MAP } from '../config/permissions';
import { isSuper, canDo } from '../utils/canDo';
import './UserMgmt.css';

/* ─────────────────────────────────────────────────
   USERS TAB
───────────────────────────────────────────────── */
function UsersTab({ roles, branches }) {
  const { auth } = useAuth();
  const [users, setUsers] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    firstName: '', lastName: '', password: '', branch: '', designation: '',
    gender: 'male', status: 'inactive', number: '', roles: ['therapist'], screens: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [permUser, setPermUser] = useState(null);
  const [pendingPerms, setPendingPerms] = useState([]);
  const [permSaving, setPermSaving] = useState(false);
  const [showTerminated, setShowTerminated] = useState(false);
  const [showBulkCopy, setShowBulkCopy] = useState(false);
  const [bulkSource, setBulkSource] = useState('');
  const [bulkTargets, setBulkTargets] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const canAssignPerms = isSuper(auth);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = () => {
    api.get(`${process.env.REACT_APP_URL}/users/all?includeInactive=true`)
      .then(r => { setUsers(r.data); setTableKey(k => k + 1); })
      .catch(e => console.error('Error fetching users:', e));
  };

  // Derive unique designations from existing users, normalized to collapse duplicates
  // e.g. "sr therapist", "Sr. Therapist", "Senior Therapist" → keep the most common form
  const existingDesignations = (() => {
    const ABBREV = { sr: 'senior', jr: 'junior', dr: 'doctor', mgr: 'manager', asst: 'assistant', dept: 'department', exec: 'executive', coord: 'coordinator' };
    const normalize = s => {
      let t = (s || '').toLowerCase().replace(/[.\-_]/g, ' ').replace(/\s+/g, ' ').trim();
      Object.entries(ABBREV).forEach(([k, v]) => { t = t.replace(new RegExp(`\\b${k}\\b`, 'g'), v); });
      return t;
    };
    const raw = users.map(u => u.designation).filter(Boolean);
    // Group by normalized key, pick the most frequent original form
    const groups = {};
    raw.forEach(d => {
      const key = normalize(d);
      if (!groups[key]) groups[key] = {};
      groups[key][d] = (groups[key][d] || 0) + 1;
    });
    return Object.values(groups).map(variants => {
      // Pick the form used most; on tie, pick the Title Case one
      const sorted = Object.entries(variants).sort((a, b) => b[1] - a[1]);
      return sorted[0][0];
    }).sort();
  })();

  const openAdd = () => {
    setForm({ firstName: '', lastName: '', password: '', branch: '', designation: '', gender: 'male', status: 'inactive', number: '', roles: ['therapist'], permissions: [], allowedOutside: false, limitedRemoteAccess: false, remoteKitAccess: false, remoteExpenseAccess: false, remoteTaskAccess: false, skipQrAttendance: false, configuredWorkHours: 8 });
    setFormErrors({});
    setIsEdit(false); setSelected(null); setShowForm(true);
  };

  const openEdit = (user) => {
    setForm({
      ...user,
      firstName:   user.firstName   || '',
      lastName:    user.lastName    || '',
      password:    '',
      designation: user.designation || '',
      permissions: user.permissions || [],
      screens:     user.screens     || [],
      configuredWorkHours: user.configuredWorkHours || 8,
    });
    setFormErrors({});
    setIsEdit(true); setSelected(user); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setSelected(null); fetchUsers(); };

  const handleRoleToggle = (rtype) => {
    const isAdding = !(form.roles || []).includes(rtype);
    if (isAdding && ['owner', 'solution'].includes(rtype)) {
      if (!window.confirm(`Warning: Assigning "${rtype}" role grants full system access.\n\nAll existing owners will be notified of this change.\n\nAre you sure you want to continue?`)) return;
    }
    setForm(f => ({
      ...f,
      roles: f.roles.includes(rtype) ? f.roles.filter(r => r !== rtype) : [...f.roles, rtype]
    }));
  };

  const handlePermToggle = (key) => {
    setForm(f => ({
      ...f,
      permissions: (f.permissions || []).includes(key)
        ? f.permissions.filter(k => k !== key)
        : [...(f.permissions || []), key],
    }));
  };

  const handlePermGroupToggle = (keys) => {
    setForm(f => {
      const current = f.permissions || [];
      const allOn = keys.every(k => current.includes(k));
      return {
        ...f,
        permissions: allOn
          ? current.filter(k => !keys.includes(k))
          : [...new Set([...current, ...keys])],
      };
    });
  };

  const setField = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setFormErrors(fe => ({ ...fe, [field]: '' }));
  };

  // ── First-name duplicate detection (warning only — BE handles actual username) ─
  const firstNameHint = (() => {
    const fn = (form.firstName || '').trim();
    const ln = (form.lastName  || '').trim();
    if (!fn || isEdit) return null;
    const conflict = users.find(u =>
      (u.firstName || u.username.split(' ')[0]).toLowerCase() === fn.toLowerCase()
    );
    if (!conflict) return null;
    const autoName = ln ? `${fn}${ln.replace(/\s+/g, '').substring(0, 2)}` : null;
    return { conflictName: conflict.username, autoName };
  })();

  const validate = () => {
    const errors = {};
    if (!(form.firstName || '').trim()) errors.firstName   = 'First name is required';
    if (!(form.designation || '').trim()) errors.designation = 'Designation is required';
    return errors;
  };

  // Normalize designation: expand abbreviations + title case
  const normalizeDesignation = (val) => {
    const ABBREV = { sr: 'Senior', jr: 'Junior', dr: 'Doctor', mgr: 'Manager', asst: 'Assistant', dept: 'Department', exec: 'Executive', coord: 'Coordinator' };
    let t = (val || '').replace(/[.\-_]/g, ' ').replace(/\s+/g, ' ').trim();
    // Expand abbreviations (case-insensitive)
    t = t.replace(/\b\w+\b/g, w => ABBREV[w.toLowerCase()] || w);
    // Title case
    t = t.replace(/\b\w/g, c => c.toUpperCase());
    // If an existing designation matches (normalized), use that exact form
    const lc = t.toLowerCase();
    const match = existingDesignations.find(d => d.toLowerCase() === lc);
    return match || t;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    // Normalize designation before saving
    form.designation = normalizeDesignation(form.designation);
    if (isEdit) {
      const payload = { ...form, name: auth.username, permissions: canAssignPerms ? (form.permissions || []) : undefined };
      api.post(`${process.env.REACT_APP_URL}/users/update/${selected._id}`, payload)
        .then(() => { alert('User updated'); closeForm(); })
        .catch(() => alert('Error updating user'));
    } else {
      const registerPayload = { ...form, permissions: canAssignPerms ? (form.permissions || []) : [] };
      api.post(`${process.env.REACT_APP_URL}/register`, registerPayload)
        .then(res => {
          const finalName = res.data?.username;
          const fn = (form.firstName || '').trim();
          const msg = finalName && finalName.toLowerCase() !== fn.toLowerCase()
            ? `User added successfully.\nUsername set to "${finalName}" (auto-adjusted to avoid duplicate first name).`
            : 'User added successfully.';
          alert(msg);
          closeForm();
        })
        .catch(err => {
          const msg = err?.response?.data?.message || 'Error adding user';
          alert(msg);
        });
    }
  };

  const handleTerminate = (user) => {
    if (!window.confirm(
      `Terminate ${user.username}?\n\nThey will lose all app access immediately.\nAll their historical data (sessions, appointments, payroll records) will be preserved.\n\nThis can be reversed by restoring the account.`
    )) return;
    api.patch(`${process.env.REACT_APP_URL}/users/terminate/${user._id}`)
      .then(() => setUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'terminated' } : u)))
      .catch(e => console.error('Error terminating user:', e));
  };

  const handleRestore = (user) => {
    if (!window.confirm(`Restore ${user.username}? They will regain app access.`)) return;
    api.patch(`${process.env.REACT_APP_URL}/users/restore/${user._id}`)
      .then(() => setUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'active' } : u)))
      .catch(e => console.error('Error restoring user:', e));
  };

  const handleDelete = (user) => {
    if (!window.confirm(
      `PERMANENTLY DELETE ${user.username}?\n\nThis cannot be undone. Their login record will be removed.\n\nNote: To simply block access, use "Terminate" instead.`
    )) return;
    api.delete(`${process.env.REACT_APP_URL}/users/delete/${user._id}`)
      .then(() => setUsers(prev => prev.filter(u => u._id !== user._id)))
      .catch(e => alert(e?.response?.data?.message || 'Error deleting user'));
  };

  const handleToggleStatus = (user) => {
    // Only toggle between active ↔ inactive (not terminated)
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const payload = { ...user, status: newStatus, name: auth.username };
    api.post(`${process.env.REACT_APP_URL}/users/update/${user._id}`, payload)
      .then(() => setUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: newStatus } : u)))
      .catch(e => console.error('Error updating status:', e));
  };

  const openPermPanel = (user) => {
    setPermUser(user);
    setPendingPerms(user.permissions || []);
  };

  const togglePerm = (key) => {
    setPendingPerms(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleGroup = (keys) => {
    const allOn = keys.every(k => pendingPerms.includes(k));
    if (allOn) {
      setPendingPerms(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setPendingPerms(prev => [...new Set([...prev, ...keys])]);
    }
  };

  const savePermissions = async () => {
    if (!permUser) return;
    setPermSaving(true);
    try {
      await api.patch(`${process.env.REACT_APP_URL}/users/permissions/${permUser._id}`, { permissions: pendingPerms });
      setUsers(prev => prev.map(u => u._id === permUser._id ? { ...u, permissions: pendingPerms } : u));
      setPermUser(null);
      alert('Permissions saved');
    } catch (e) {
      alert(e?.response?.data?.message || 'Error saving permissions');
    } finally {
      setPermSaving(false);
    }
  };

  const bulkCopyPermissions = async () => {
    const srcUser = users.find(u => u._id === bulkSource);
    if (!srcUser || bulkTargets.length === 0) return;
    const perms = srcUser.permissions || [];
    setBulkSaving(true);
    let ok = 0, fail = 0;
    for (const tid of bulkTargets) {
      try {
        await api.patch(`${process.env.REACT_APP_URL}/users/permissions/${tid}`, { permissions: perms });
        ok++;
      } catch { fail++; }
    }
    setUsers(prev => prev.map(u => bulkTargets.includes(u._id) ? { ...u, permissions: [...perms] } : u));
    setBulkSaving(false);
    setShowBulkCopy(false); setBulkSource(''); setBulkTargets([]);
    alert(`Permissions copied to ${ok} user${ok !== 1 ? 's' : ''}${fail ? `, ${fail} failed` : ''}.`);
  };

  // Role hierarchy level (higher = more senior)
  const _roleLevel = (role) => {
    switch (role) {
      case 'owner': return 100;
      case 'solution': return 90;
      case 'supervisor': return 80;
      case 'admin': case 'superadmin': return 60;
      default: return 10; // therapist, receptionist, etc.
    }
  };
  const myMaxLevel = Math.max(...(auth.roles || []).map(_roleLevel), 0);

  // Filter: hide users whose highest role exceeds viewer's level
  const _userMaxLevel = (u) => Math.max(...(u.roles || []).map(_roleLevel), 0);
  const displayedUsers = (showTerminated
    ? users.filter(u => u.status === 'terminated')
    : users.filter(u => u.status !== 'terminated')
  ).filter(u => _userMaxLevel(u) <= myMaxLevel);

  const columns = [
    { name: 'ID',          selector: r => r.age,        sortable: true, width: '60px' },
    { name: 'Name',        selector: r => r.firstName ? `${r.firstName} ${r.lastName}`.trim() : r.username, sortable: true,
      cell: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.firstName ? `${r.firstName} ${r.lastName}`.trim() : r.username}</div>
          {r.firstName && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.username}</div>}
        </div>
      )
    },
    { name: 'Number',      selector: r => r.number, width: '110px' },
    { name: 'Designation', selector: r => r.designation, width: '110px' },
    { name: 'Roles', width: '140px',
      selector: r => (r.roles || []).join(', '),
      cell: r => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {(r.roles || []).map((role, i) => (
            <span key={i} style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 600, background: '#ede9fe', color: '#5b21b6' }}>{role}</span>
          ))}
        </div>
      )
    },
    { name: 'Status',      selector: r => r.status,     sortable: true, width: '100px',
      cell: r => <span className={`um-badge um-badge-${r.status}`}>{r.status}</span> },
    {
      name: 'Actions', width: canAssignPerms ? '200px' : '170px',
      cell: r => {
        const isTerminated = r.status === 'terminated';
        return (
          <div className="um-actions">
            <button className="um-btn-icon" title="Edit" onClick={() => openEdit(r)}><FaEdit /></button>
            {isTerminated ? (
              <>
                <button className="um-btn-icon um-btn-success" title="Restore account" onClick={() => handleRestore(r)}><FaUndo /></button>
                {canAssignPerms && (
                  <button className="um-btn-icon um-btn-danger" title="Permanently delete" onClick={() => handleDelete(r)}><FaTrash /></button>
                )}
              </>
            ) : (
              <>
                <button className={`um-btn-icon ${r.status === 'active' ? 'um-btn-warn' : 'um-btn-success'}`}
                  title={r.status === 'active' ? 'Deactivate (temporary)' : 'Activate'}
                  onClick={() => handleToggleStatus(r)}>
                  {r.status === 'active' ? <FaBan /> : <FaCheck />}
                </button>
                <button className="um-btn-icon um-btn-terminate" title="Terminate account"
                  onClick={() => handleTerminate(r)}><FaUserTimes /></button>
                {canAssignPerms && (
                  <button className="um-btn-icon um-btn-perm" title="Assign Permissions"
                    onClick={() => openPermPanel(r)}><FaKey /></button>
                )}
              </>
            )}
          </div>
        );
      }
    }
  ];

  const ExpandedRow = ({ data: r }) => (
    <div style={{ padding: '12px 20px 12px 60px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        {/* Branch */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Branch</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{r.branch || '\u2014'}</div>
        </div>
        {/* Roles */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Roles</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(r.roles || []).map(function(role, i) {
              return <span key={i} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#ede9fe', color: '#5b21b6' }}>{role}</span>;
            })}
            {r.allowedOutside && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}>{'\uD83C\uDF10'} Full Remote</span>
            )}
            {r.limitedRemoteAccess && !r.allowedOutside && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#fff7ed', color: '#c2410c' }}>{'\uD83D\uDCF1'} Remote Dashboard</span>
            )}
            {r.remoteKitAccess && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#d1fae5', color: '#065f46' }}>Remote KIT</span>
            )}
            {r.remoteExpenseAccess && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#cffafe', color: '#155e75' }}>Remote Expenses</span>
            )}
            {r.remoteTaskAccess && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#fae8ff', color: '#86198f' }}>Remote Tasks</span>
            )}
            {r.skipQrAttendance && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#3730a3' }}>Skip QR</span>
            )}
          </div>
        </div>
        {/* Permissions */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Permissions</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(r.permissions && r.permissions.length > 0) ? r.permissions.map(function(p, i) {
              return <span key={i} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>{p}</span>;
            }) : <span style={{ fontSize: 12, color: '#94a3b8' }}>Default role permissions</span>}
          </div>
        </div>
      </div>
      {/* Extra info row */}
      <div style={{ display: 'flex', gap: 24, marginTop: 10, fontSize: 12, color: '#64748b' }}>
        {r.gender && <span>Gender: {r.gender}</span>}
        {r.email && <span>Email: {r.email}</span>}
        {r.address && <span>Address: {r.address}</span>}
      </div>
    </div>
  );

  if (showForm) {
    return (
      <div className="um-form-page">
        <div className="um-form-header">
          <h3>{isEdit ? 'Edit User' : 'Add New User'}</h3>
          <button className="um-btn-close" onClick={closeForm}><FaTimes /> Close</button>
        </div>
        <form className="um-form" onSubmit={handleSubmit} noValidate>
          {/* Duplicate from existing user — only in Add mode */}
          {!isEdit && users.length > 0 && (
            <div className="um-form-group" style={{ marginBottom: 14 }}>
              <label style={{ color: '#6366f1', fontWeight: 600 }}>Copy from existing user</label>
              <select
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid #c7d2fe', fontSize: 14 }}
                value=""
                onChange={e => {
                  const u = users.find(x => x._id === e.target.value);
                  if (!u) return;
                  setForm({
                    firstName: '', lastName: '', password: '',
                    branch: u.branch || '',
                    designation: u.designation || '',
                    gender: u.gender || 'male',
                    status: 'inactive',
                    number: '',
                    roles: [...(u.roles || ['therapist'])],
                    permissions: [...(u.permissions || [])],
                    screens: [...(u.screens || [])],
                    allowedOutside: u.allowedOutside || false,
                    limitedRemoteAccess: u.limitedRemoteAccess || false,
                    remoteKitAccess: u.remoteKitAccess || false,
                    remoteExpenseAccess: u.remoteExpenseAccess || false,
                    remoteTaskAccess: u.remoteTaskAccess || false,
                    skipQrAttendance: u.skipQrAttendance || false,
                    configuredWorkHours: u.configuredWorkHours || 8,
                    homeSiteId: u.homeSiteId || 'P',
                  });
                }}
              >
                <option value="">— Select to copy roles, permissions & settings —</option>
                {users.filter(u => u.status !== 'terminated').map(u => (
                  <option key={u._id} value={u._id}>{u.firstName} {u.lastName} ({u.designation})</option>
                ))}
              </select>
            </div>
          )}
          <div className="um-form-grid">

            {/* First Name */}
            <div className="um-form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={form.firstName}
                placeholder="e.g. Priya"
                className={formErrors.firstName ? 'um-input-error' : firstNameHint ? 'um-input-warn' : ''}
                onChange={e => setField('firstName', e.target.value)}
              />
              {formErrors.firstName && <span className="um-error">{formErrors.firstName}</span>}
              {!formErrors.firstName && firstNameHint && (
                <span style={{ fontSize: 12, color: '#b45309', marginTop: 4, display: 'block' }}>
                  ⚠ "{firstNameHint.conflictName}" already exists with this first name.
                  {firstNameHint.autoName
                    ? ` Username will be auto-set to "${firstNameHint.autoName}".`
                    : ' Add a last name so the system can generate a unique username.'}
                </span>
              )}
            </div>

            {/* Last Name */}
            <div className="um-form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={form.lastName}
                placeholder="e.g. Sharma"
                onChange={e => setField('lastName', e.target.value)}
              />
              {!isEdit && (form.firstName || '').trim() && (
                <span style={{ fontSize: 11, color: '#6b7280', marginTop: 3, display: 'block' }}>
                  Username will be: <strong style={{ color: '#3730a3' }}>
                    {firstNameHint?.autoName || (form.firstName || '').trim()}
                  </strong> (auto-generated by system)
                </span>
              )}
              {isEdit && selected?.username && (
                <span style={{ fontSize: 11, color: '#6b7280', marginTop: 3, display: 'block' }}>
                  Username (used in all records): <strong style={{ color: '#3730a3' }}>{selected.username}</strong>
                </span>
              )}
            </div>

            {/* Password */}
            <div className="um-form-group">
              <label>Password {isEdit ? '(leave blank to keep)' : '*'}</label>
              <input
                type="password"
                value={form.password}
                placeholder={isEdit ? 'Leave blank to keep current' : 'Enter password'}
                className={formErrors.password ? 'um-input-error' : ''}
                onChange={e => setField('password', e.target.value)}
              />
              {formErrors.password && <span className="um-error">{formErrors.password}</span>}
            </div>

            {/* Designation — combo: select existing OR type new */}
            <div className="um-form-group">
              <label>Designation *</label>
              <input
                type="text"
                list="um-designation-list"
                value={form.designation}
                placeholder="Select from list or type a new designation"
                className={formErrors.designation ? 'um-input-error' : ''}
                onChange={e => setField('designation', e.target.value)}
              />
              <datalist id="um-designation-list">
                {existingDesignations.map(d => <option key={d} value={d} />)}
              </datalist>
              {formErrors.designation
                ? <span className="um-error">{formErrors.designation}</span>
                : existingDesignations.length > 0 && (
                    <span className="um-desig-hint">
                      {existingDesignations.length} existing designation{existingDesignations.length > 1 ? 's' : ''} available — or type a new one
                    </span>
                  )
              }
            </div>

            {/* Number */}
            <div className="um-form-group">
              <label>Number</label>
              <input
                type="text"
                value={form.number}
                placeholder="Phone number"
                onChange={e => setField('number', e.target.value)}
              />
            </div>

            {/* Branch */}
            <div className="um-form-group">
              <label>Branch</label>
              <select value={form.branch} onChange={e => setField('branch', e.target.value)}>
                <option value="">Select Branch</option>
                {branches.filter(b => b.isActive !== false && b.status !== 'inactive').map(b => <option key={b._id} value={b.name}>{b.name}</option>)}
              </select>
            </div>

            {/* Gender */}
            <div className="um-form-group">
              <label>Gender</label>
              <select value={form.gender} onChange={e => setField('gender', e.target.value)}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="noanswer">Prefer Not to Answer</option>
              </select>
            </div>

            {/* Status */}
            <div className="um-form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => setField('status', e.target.value)}>
                <option value="inactive">Inactive</option>
                <option value="active">Active</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>

          </div>

          {/* Remote Access Flags — visible to owner / solution / supervisor */}
          {(canAssignPerms || canDo(auth, 'users.edit')) && (
            <div style={{ padding: '10px 0 6px', borderTop: '1px solid #f0f0f0', marginBottom: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Geo-fence & Remote Access</div>

              {/* ── Section 1: Full Remote Access ── */}
              {canAssignPerms && (
              <div style={{ background: form.allowedOutside ? '#eff6ff' : '#f8fafc', border: '1.5px solid', borderColor: form.allowedOutside ? '#93c5fd' : '#e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={!!form.allowedOutside}
                    onChange={e => {
                      setField('allowedOutside', e.target.checked);
                      if (e.target.checked) { setField('limitedRemoteAccess', false); setField('remoteKitAccess', false); setField('remoteExpenseAccess', false); setField('remoteTaskAccess', false); }
                    }}
                    style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>
                      Full Remote Access
                    </span>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      All authorized screens accessible from any location. Geo-fence and IP checks fully skipped.
                    </div>
                  </div>
                </label>
              </div>
              )}

              {/* ── Section 2: Limited Remote Access (individual flags) ── */}
              {!form.allowedOutside && (
              <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>Limited Remote Access</div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={!!form.limitedRemoteAccess}
                    onChange={e => setField('limitedRemoteAccess', e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: '#f59e0b', marginTop: 2 }}
                  />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>Remote Therapist Dashboard</span>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Therapist Dashboard, Profile & Notifications from outside clinic.</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', userSelect: 'none', marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!form.remoteKitAccess}
                    onChange={e => setField('remoteKitAccess', e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: '#10b981', marginTop: 2 }}
                  />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>Remote KIT Access</span>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>KIT (leads) screen from outside geo-fence. Can combine with Dashboard above.</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', userSelect: 'none', marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!form.remoteExpenseAccess}
                    onChange={e => setField('remoteExpenseAccess', e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: '#0891b2', marginTop: 2 }}
                  />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#155e75' }}>Remote Expense Access</span>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Expenses screen & kiosk expenses from outside geo-fence. Requires expenses.view permission.</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', userSelect: 'none', marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!form.remoteTaskAccess}
                    onChange={e => setField('remoteTaskAccess', e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: '#a855f7', marginTop: 2 }}
                  />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#86198f' }}>Remote Task Access</span>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Tasks & checklists from outside geo-fence.</div>
                  </div>
                </label>
              </div>
              )}

              {/* ── Skip QR Attendance (separate, always visible) ── */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={!!form.skipQrAttendance}
                  onChange={e => setField('skipQrAttendance', e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: '#6366f1', marginTop: 2 }}
                />
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#3730a3' }}>Skip QR Attendance</span>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Exempt from daily QR check-in. No auto-leaves for missed scans.</div>
                </div>
              </label>

              {/* ── Daily Work Hours ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#3730a3', whiteSpace: 'nowrap' }}>Daily Work Hours</label>
                <input
                  type="number"
                  min={1} max={16} step={0.5}
                  value={form.configuredWorkHours || 8}
                  onChange={e => setField('configuredWorkHours', parseFloat(e.target.value) || 8)}
                  style={{ width: 60, padding: '4px 6px', borderRadius: 6, border: '1.5px solid #cbd5e1', fontSize: 13, textAlign: 'center' }}
                />
                <span style={{ fontSize: 11, color: '#9ca3af' }}>hrs/day (for reports)</span>
              </div>
            </div>
          )}

          <div className="um-roles-section">
            <label className="um-roles-label">Role (functional classification)</label>
            {canAssignPerms ? (
              <div className="um-roles-grid">
                {roles.map(role => (
                  <label key={role._id} className="um-checkbox-label">
                    <input type="checkbox" checked={(form.roles || []).includes(role.rtype)}
                      onChange={() => handleRoleToggle(role.rtype)} />
                    <span className="um-checkbox-text">{role.rtype}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0' }}>
                {(form.roles || []).map(r => (
                  <span key={r} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#e0e7ff', color: '#3730a3' }}>{r}</span>
                ))}
              </div>
            )}
          </div>

          {canAssignPerms && (
            <div className="um-roles-section" style={{ borderTop: '2px solid #e0e7ff', paddingTop: 14, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <FaKey style={{ color: '#6366f1', fontSize: 13 }} />
                <label className="um-roles-label" style={{ margin: 0 }}>Permissions</label>
                <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>
                  {(form.permissions || []).length} selected
                </span>
                {(form.permissions || []).length > 0 && (
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, permissions: [] }))}
                    style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Clear all
                  </button>
                )}
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <span style={{ color: '#6b7280' }}>Copy from:</span>
                  <select
                    value=""
                    onChange={e => {
                      const src = users.find(u => u._id === e.target.value);
                      if (src) setForm(f => ({ ...f, permissions: src.permissions || [] }));
                    }}
                    style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid #c7d2fe', color: '#3730a3', cursor: 'pointer' }}
                  >
                    <option value="">— select user —</option>
                    {users.filter(u => u._id !== selected?._id).map(u => (
                      <option key={u._id} value={u._id}>
                        {u.username}{u.designation ? ` (${u.designation})` : ''}
                        {(u.permissions || []).length > 0 ? ` · ${u.permissions.length} perms` : ''}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              {PERMISSION_GROUPS.map(group => {
                const perms = form.permissions || [];
                const allOn = group.keys.every(k => perms.includes(k));
                const someOn = group.keys.some(k => perms.includes(k));
                return (
                  <div key={group.group} style={{ marginBottom: 10 }}>
                    <label style={{ fontWeight: 700, fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 4 }}>
                      <input type="checkbox" checked={allOn}
                        ref={el => { if (el) el.indeterminate = someOn && !allOn; }}
                        onChange={() => handlePermGroupToggle(group.keys)} />
                      {group.group}
                      {group.screen && <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500, marginLeft: 6 }}>→ {group.screen}</span>}
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', paddingLeft: 20 }}>
                      {group.keys.map(key => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', minWidth: 220 }}>
                          <input type="checkbox" checked={perms.includes(key)} onChange={() => handlePermToggle(key)} />
                          <span style={{ color: '#374151' }}>{PERMISSION_MAP[key] || key}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="um-form-actions">
            <button type="submit" className="um-btn-primary">
              {isEdit ? 'Update User' : 'Add User'}
            </button>
            <button type="button" className="um-btn-secondary" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  const terminatedCount = users.filter(u => u.status === 'terminated').length;
  const tableData = { columns, data: displayedUsers };
  return (
    <div>
      <div className="um-tab-toolbar">
        <button className="um-btn-primary" onClick={openAdd}><FaPlus /> Add User</button>
        <button
          type="button"
          onClick={() => setShowTerminated(s => !s)}
          style={{
            marginLeft: 12, fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
            background: showTerminated ? '#fef2f2' : '#f3f4f6',
            color: showTerminated ? '#dc2626' : '#6b7280',
            border: `1px solid ${showTerminated ? '#fca5a5' : '#e5e7eb'}`,
            fontWeight: showTerminated ? 700 : 400,
          }}
        >
          {showTerminated ? 'Show Active / Inactive' : `Terminated${terminatedCount > 0 ? ` (${terminatedCount})` : ''}`}
        </button>
        {canAssignPerms && (
          <button type="button" onClick={() => { setShowBulkCopy(true); setBulkSource(''); setBulkTargets([]); }}
            style={{ marginLeft: 12, fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', fontWeight: 600 }}>
            <FaKey style={{ marginRight: 4 }} /> Bulk Copy Permissions
          </button>
        )}
      </div>
      <DataTableExtensions key={tableKey + (showTerminated ? '-t' : '')} {...tableData} filter filterLabel={false}>
        <DataTable columns={columns} data={users} pagination highlightOnHover striped
          pointerOnHover paginationPerPage={50} persistTableHead
          fixedHeader fixedHeaderScrollHeight="60vh" defaultSortAsc
          expandableRows expandableRowsComponent={ExpandedRow} />
      </DataTableExtensions>

      {/* ── Permission Assignment Panel ── */}
      {permUser && (
        <div style={{ marginTop: 24, border: '2px solid #6366f1', borderRadius: 12, padding: '16px 20px', background: '#f5f3ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <FaKey style={{ color: '#6366f1' }} />
            <strong style={{ fontSize: 15, color: '#3730a3' }}>
              Assign Permissions — {permUser.username}
            </strong>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
              {pendingPerms.length} permission{pendingPerms.length !== 1 ? 's' : ''} selected
            </span>
            <button onClick={() => setPermUser(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6366f1' }}>×</button>
          </div>

          {PERMISSION_GROUPS.map(group => {
            const allOn = group.keys.every(k => pendingPerms.includes(k));
            const someOn = group.keys.some(k => pendingPerms.includes(k));
            return (
              <div key={group.group} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <label style={{ fontWeight: 700, fontSize: 12, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={allOn} ref={el => { if (el) el.indeterminate = someOn && !allOn; }}
                      onChange={() => toggleGroup(group.keys)} />
                    {group.group}
                    {group.screen && <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500, marginLeft: 6 }}>→ {group.screen}</span>}
                  </label>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', paddingLeft: 18 }}>
                  {group.keys.map(key => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', minWidth: 200 }}>
                      <input type="checkbox" checked={pendingPerms.includes(key)} onChange={() => togglePerm(key)} />
                      <span style={{ color: '#374151' }}>{PERMISSION_MAP[key] || key}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={savePermissions} disabled={permSaving}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {permSaving ? 'Saving…' : 'Save Permissions'}
            </button>
            <button onClick={() => setPermUser(null)}
              style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Bulk Copy Permissions Modal ── */}
      {showBulkCopy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowBulkCopy(false); }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '90%', maxWidth: 520, maxHeight: '80vh', overflow: 'auto', padding: '20px 24px', boxShadow: '0 8px 30px rgba(0,0,0,.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#3730a3' }}>Bulk Copy Permissions</h3>
              <button onClick={() => setShowBulkCopy(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>x</button>
            </div>

            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Copy FROM (source user):</label>
            <select value={bulkSource} onChange={e => setBulkSource(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #c7d2fe', fontSize: 13, marginBottom: 14 }}>
              <option value="">— Select source user —</option>
              {users.filter(u => u.status !== 'terminated').map(u => (
                <option key={u._id} value={u._id}>
                  {u.firstName ? `${u.firstName} ${u.lastName}`.trim() : u.username} ({u.designation || 'no title'}) — {(u.permissions || []).length} perms
                </option>
              ))}
            </select>

            {bulkSource && (
              <>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                  Source has {(users.find(u => u._id === bulkSource)?.permissions || []).length} permissions
                </div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                  Copy TO (select target users):
                </label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button type="button" onClick={() => setBulkTargets(users.filter(u => u._id !== bulkSource && u.status !== 'terminated').map(u => u._id))}
                    style={{ fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Select all</button>
                  <button type="button" onClick={() => setBulkTargets([])}
                    style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear all</button>
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px' }}>
                  {users.filter(u => u._id !== bulkSource && u.status !== 'terminated').map(u => (
                    <label key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                      <input type="checkbox" checked={bulkTargets.includes(u._id)}
                        onChange={e => setBulkTargets(prev => e.target.checked ? [...prev, u._id] : prev.filter(id => id !== u._id))}
                        style={{ width: 15, height: 15, accentColor: '#6366f1' }} />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{u.firstName ? `${u.firstName} ${u.lastName}`.trim() : u.username}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{u.designation || ''} — {(u.permissions || []).length} perms</span>
                    </label>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={bulkCopyPermissions} disabled={bulkSaving || bulkTargets.length === 0}
                    style={{ background: bulkTargets.length === 0 ? '#9ca3af' : '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, fontSize: 13, cursor: bulkTargets.length === 0 ? 'not-allowed' : 'pointer' }}>
                    {bulkSaving ? 'Copying…' : `Copy to ${bulkTargets.length} user${bulkTargets.length !== 1 ? 's' : ''}`}
                  </button>
                  <button onClick={() => setShowBulkCopy(false)}
                    style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   ROLES TAB
───────────────────────────────────────────────── */
function RolesTab({ onRolesChange }) {
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ rtype: '', description: '' });

  // Roles that cannot be edited or deleted
  const PROTECTED_ROLES = ['owner', 'solution'];
  // Roles that cannot be deleted (but can be edited)
  const CORE_ROLES = ['admin', 'therapist', 'receptionist', 'housekeeping', 'saadhak', 'supervisor'];

  useEffect(() => { fetchRoles(); }, []);

  const fetchRoles = () => {
    api.get(`${process.env.REACT_APP_URL}/roles`)
      .then(r => { setRoles(r.data); if (onRolesChange) onRolesChange(r.data); })
      .catch(e => console.error('Error fetching roles:', e));
  };

  const openAdd = () => {
    setForm({ rtype: '', description: '' });
    setIsEdit(false); setSelected(null); setShowForm(true);
  };

  const openEdit = (role) => {
    if (PROTECTED_ROLES.includes(role.rtype)) return;
    setForm({ rtype: role.rtype, description: role.description || '' });
    setIsEdit(true); setSelected(role); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setSelected(null); fetchRoles(); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.rtype.trim()) { alert('Role name is required'); return; }
    if (isEdit) {
      api.post(`${process.env.REACT_APP_URL}/roles/update/${selected._id}`, form)
        .then(() => { alert('Role updated'); closeForm(); })
        .catch(() => alert('Error updating role'));
    } else {
      api.post(`${process.env.REACT_APP_URL}/roles/add`, form)
        .then(() => { alert('Role added'); closeForm(); })
        .catch(() => alert('Error adding role'));
    }
  };

  const handleDelete = (role) => {
    if (PROTECTED_ROLES.includes(role.rtype) || CORE_ROLES.includes(role.rtype)) {
      alert(`Cannot delete "${role.rtype}" — it is a core system role.`);
      return;
    }
    if (!window.confirm(`Delete role "${role.rtype}"?`)) return;
    api.delete(`${process.env.REACT_APP_URL}/roles/delete/${role._id}`)
      .then(() => { setRoles(roles.filter(r => r._id !== role._id)); if (onRolesChange) onRolesChange(roles.filter(r => r._id !== role._id)); })
      .catch(e => console.error('Error deleting role:', e));
  };

  const columns = [
    { name: 'Role Name', selector: r => r.rtype, sortable: true,
      cell: r => (
        <span>
          {r.rtype}
          {PROTECTED_ROLES.includes(r.rtype) && <span style={{ fontSize: 10, color: '#dc2626', marginLeft: 6 }}>system</span>}
          {CORE_ROLES.includes(r.rtype) && <span style={{ fontSize: 10, color: '#2563eb', marginLeft: 6 }}>core</span>}
        </span>
      )
    },
    { name: 'Description', selector: r => r.description || '—', grow: 2 },
    {
      name: 'Actions', width: '100px',
      cell: r => {
        const isProtected = PROTECTED_ROLES.includes(r.rtype);
        const isCore = CORE_ROLES.includes(r.rtype);
        return (
          <div className="um-actions">
            {!isProtected && <button className="um-btn-icon" title="Edit" onClick={() => openEdit(r)}><FaEdit /></button>}
            {!isProtected && !isCore && <button className="um-btn-icon um-btn-danger" title="Delete" onClick={() => handleDelete(r)}><FaTrash /></button>}
          </div>
        );
      }
    }
  ];

  if (showForm) {
    return (
      <div className="um-form-page">
        <div className="um-form-header">
          <h3>{isEdit ? 'Edit Role' : 'Add New Role'}</h3>
          <button className="um-btn-close" onClick={closeForm}><FaTimes /> Close</button>
        </div>
        <form className="um-form" onSubmit={handleSubmit}>
          <div className="um-form-grid">
            <div className="um-form-group">
              <label>Role Name (identifier) *</label>
              <input type="text" required value={form.rtype}
                placeholder="e.g. frontdesk, therapist, finance"
                onChange={e => setForm({ ...form, rtype: e.target.value.toLowerCase().replace(/\s+/g, '') })} />
              <small className="um-hint">Used internally. No spaces. Lowercase only.</small>
            </div>
            <div className="um-form-group">
              <label>Description</label>
              <input type="text" value={form.description}
                placeholder="e.g. Front desk staff access"
                onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <div className="um-form-actions">
            <button type="submit" className="um-btn-primary">
              {isEdit ? 'Update Role' : 'Add Role'}
            </button>
            <button type="button" className="um-btn-secondary" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  const tableData = { columns, data: roles };
  return (
    <div>
      <div className="um-tab-toolbar">
        <button className="um-btn-primary" onClick={openAdd}><FaPlus /> Add Role</button>
        <button className="um-btn-secondary" style={{ marginLeft: 8 }} onClick={async () => {
          if (!window.confirm('Remove stale roles from all users? (Roles not in the Roles table will be stripped from user data)')) return;
          try {
            const r = await api.post(`${process.env.REACT_APP_URL}/roles/cleanup-stale`);
            const d = r.data;
            if (d.cleaned === 0) { alert('No stale roles found — all users are clean.'); return; }
            alert(`Cleaned ${d.cleaned} user(s):\n` + d.details.map(u => `${u.username}: removed [${u.removed.join(', ')}]`).join('\n'));
          } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
        }}>Cleanup Stale Roles</button>
      </div>
      <DataTableExtensions {...tableData} filter filterLabel={false}>
        <DataTable columns={columns} data={roles} pagination highlightOnHover striped
          pointerOnHover dense paginationPerPage={50} persistTableHead
          fixedHeader fixedHeaderScrollHeight="60vh" />
      </DataTableExtensions>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   PERMISSIONS TAB — Master permission reference list
───────────────────────────────────────────────── */
function PermissionsTab() {
  return (
    <div style={{ padding: '8px 0' }}>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        These are all available permissions in the system. Use the <FaKey style={{ verticalAlign: 'middle', color: '#6366f1' }} /> button on a user row (Users tab) to assign permissions to that user.
      </p>
      {PERMISSION_GROUPS.map(group => (
        <div key={group.group} style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#3730a3', marginBottom: 6, borderBottom: '1px solid #e0e7ff', paddingBottom: 4 }}>
            {group.group}
            {group.screen && <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500, marginLeft: 8 }}>→ {group.screen}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 24px' }}>
            {group.keys.map(key => (
              <div key={key} style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 280, fontSize: 12, padding: '2px 0' }}>
                <code style={{ background: '#ede9fe', color: '#4338ca', borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace', fontSize: 11 }}>{key}</code>
                <span style={{ color: '#6b7280' }}>{PERMISSION_MAP[key]}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   MAIN UserMgmt COMPONENT
───────────────────────────────────────────────── */
const TABS = ['Users', 'Roles', 'Permissions'];

export default function UserMgmt() {
  const [activeTab, setActiveTab] = useState('Users');
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/roles`)
      .then(r => setRoles(r.data))
      .catch(e => console.error('Error loading roles:', e));

    api.get(`${process.env.REACT_APP_URL}/branches`)
      .then(r => setBranches(r.data))
      .catch(e => console.error('Error loading branches:', e));
  }, []);

  return (
    <div className="um-container">
      <div className="um-header">
        <h2>User Management</h2>
        <p className="um-subtitle">Manage users, roles, and permissions. Roles control which screens each user can access.</p>
      </div>

      <div className="um-tabs">
        {TABS.map(tab => (
          <button key={tab}
            className={`um-tab${activeTab === tab ? ' um-tab-active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab}
          </button>
        ))}
      </div>

      <div className="um-tab-content">
        {activeTab === 'Users' && <UsersTab roles={roles} branches={branches} />}
        {activeTab === 'Roles' && <RolesTab onRolesChange={setRoles} />}
        {activeTab === 'Permissions' && <PermissionsTab />}
      </div>
    </div>
  );
}
